import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Search, Plus, X, Filter, MapPin } from 'lucide-react';
import { agentAppAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import AddCustomerModal from '../../components/agent/AddCustomerModal';

const FILTERS = [
  { key: '', label: 'All' },
  { key: 'dueToday', label: 'Due Today' },
  { key: 'dueTomorrow', label: 'Due Tomorrow' },
  { key: 'unpaid', label: 'Unpaid' },
];

const EMPTY_ADV = { status: '', area: [], dueDate: '', paymentType: '' };

const AVATAR_COLORS = ['#ef4444', '#0f172a', '#b45309', '#2563eb', '#0891b2', '#7c3aed'];
const colorFor = (name) => AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

// Filters/toggles survive going into a customer and coming back (sessionStorage, so a
// fresh login/tab starts clean but in-app navigation keeps the agent's working set).
const LIST_STATE_KEY = 'agentCustomerListState';
const readSavedListState = () => {
  try { return JSON.parse(sessionStorage.getItem(LIST_STATE_KEY)) || {}; } catch { return {}; }
};

export default function AgentCustomerListPage() {
  const { setHeader } = useOutletContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saved] = useState(readSavedListState);
  const [filter, setFilter] = useState(saved.filter || '');
  const [search, setSearch] = useState(saved.search || '');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [adv, setAdv] = useState(saved.adv || EMPTY_ADV);
  const [draft, setDraft] = useState(EMPTY_ADV);
  const [filterOpen, setFilterOpen] = useState(false);
  const [areaOptions, setAreaOptions] = useState([]);
  const [collectionMode, setCollectionMode] = useState(!!saved.collectionMode);
  const [typeCounts, setTypeCounts] = useState(null); // { paid, unpaid, advance } — Unpaid chip + filter sheet

  // Loaded on mount for the Unpaid chip's count, refreshed whenever the filter sheet opens or
  // Collection Mode is flipped — the counts are scoped by collectionMode server-side, so they
  // always describe the round currently on screen rather than the whole book.
  useEffect(() => {
    agentAppAPI.getCustomerCounts(collectionMode ? { collectionMode: 'true' } : undefined)
      .then(({ data }) => setTypeCounts(data?.data || null)).catch(() => {});
  }, [filterOpen, collectionMode]);

  useEffect(() => {
    try { sessionStorage.setItem(LIST_STATE_KEY, JSON.stringify({ filter, search, adv, collectionMode })); } catch {}
  }, [filter, search, adv, collectionMode]);

  const advActive = Boolean(adv.status) || adv.area.length > 0 || Boolean(adv.dueDate) || Boolean(adv.paymentType);

  useEffect(() => {
    agentAppAPI.getCustomers({}).then(({ data }) => {
      const areas = [...new Set((data.data || []).map((c) => c.address?.area).filter(Boolean))].sort();
      setAreaOptions(areas);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setHeader({
      title: 'Cable Customers', showBack: false,
      menu: (
        <button
          onClick={() => { setDraft(adv); setFilterOpen(true); }}
          style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 6, position: 'relative' }}
        >
          <Filter size={20} />
          {advActive && (
            <span style={{ position: 'absolute', top: 4, right: 4, width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)' }} />
          )}
        </button>
      ),
    });
  }, [setHeader, adv, advActive]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (collectionMode) params.collectionMode = 'true';
      if (advActive) {
        if (adv.status) params.status = adv.status;
        if (adv.area.length) params.area = adv.area.join(',');
        if (adv.dueDate) params.dueDate = adv.dueDate;
        if (adv.paymentType) params.paymentType = adv.paymentType;
      } else if (filter) {
        params.filter = filter;
      }
      const { data } = await agentAppAPI.getCustomers(params);
      setCustomers(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [filter, search, adv, advActive, collectionMode]);

  useEffect(() => { load(); }, [load]);

  const canAdd = user?.role === 'admin' || user?.permissions?.addCustomers;

  // "Society" is address.area (same field labelled "Society Name" on the admin add-customer
  // form) — grouped here so agents can see which society/building a customer belongs to.
  // Within a society, rows must read in physical house-number order (1,2,3…10), not whatever
  // order the server happened to return (billing number) — same numeric-aware compare as
  // CustomersPage.js's "select area -> auto-arrange" (see [[project_customers_area_sort_bug]]),
  // this list just never had the sort step at all.
  const groupedBySociety = useMemo(() => {
    const map = new Map();
    for (const c of customers) {
      const key = c.address?.area || 'Unassigned';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    // "4-F" sequences with "4" (same unit, not a separate one) — strip the suffix before
    // the numeric compare below runs.
    const houseNo = (c) => (c.catv?.houseNumber || c.broadband?.houseNumber || '').replace(/-F$/i, '').trim();
    for (const list of map.values()) {
      list.sort((a, b) => {
        const ha = houseNo(a), hb = houseNo(b);
        if (!ha && !hb) return 0;
        if (!ha) return 1;
        if (!hb) return -1;
        return ha.localeCompare(hb, undefined, { numeric: true, sensitivity: 'base' });
      });
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [customers]);

  return (
    <div style={{ position: 'relative', minHeight: '100%' }}>
      {/* Collection Mode — one switch that narrows the list to the agent's actual
          collection round: active regular customers with a due balance. */}
      <div
        onClick={() => setCollectionMode((v) => !v)} role="switch" aria-checked={collectionMode}
        style={{
          margin: '10px 12px 0', padding: '10px 12px', borderRadius: 10, cursor: 'pointer', userSelect: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          background: collectionMode ? '#f0fdf4' : 'var(--bg-card)',
          border: `1.5px solid ${collectionMode ? '#16a34a' : 'var(--border)'}`,
        }}
      >
        <div>
          <div style={{ fontSize: '.95rem', fontWeight: 700, color: collectionMode ? '#15803d' : 'var(--text)' }}>
            Collection Mode
          </div>
          <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
            {collectionMode ? 'Active regular customers with due amount' : 'Show only customers to collect from'}
          </div>
        </div>
        <span style={{
          width: 46, height: 26, borderRadius: 13, position: 'relative', flexShrink: 0,
          background: collectionMode ? '#16a34a' : '#d1d5db', transition: 'background .15s',
        }}>
          <span style={{
            position: 'absolute', top: 2, left: collectionMode ? 22 : 2, width: 22, height: 22,
            borderRadius: '50%', background: '#fff', transition: 'left .15s', boxShadow: '0 1px 2px rgba(0,0,0,.25)',
          }} />
        </span>
      </div>

      <div style={{ padding: '10px 12px', display: 'flex', gap: 8 }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-light)' }} />
          <input
            className="form-control"
            placeholder="Search name / mobile / code"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 32 }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '0 12px 10px', WebkitOverflowScrolling: 'touch' }}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => { setAdv(EMPTY_ADV); setFilter(f.key); }}
            style={{
              flexShrink: 0, padding: '6px 12px', borderRadius: 20, fontSize: '.75rem', fontWeight: 600,
              border: '1px solid var(--border)', cursor: 'pointer', whiteSpace: 'nowrap',
              background: !advActive && filter === f.key ? 'var(--accent)' : 'var(--bg-card)',
              color: !advActive && filter === f.key ? '#fff' : 'var(--text)'
            }}
          >
            {f.label}{f.key === 'unpaid' && typeCounts ? ` (${typeCounts.unpaid})` : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading…</div>
      ) : customers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No customers found</div>
      ) : (
        groupedBySociety.map(([society, list]) => (
          <div key={society}>
            <div style={{
              padding: '7px 14px', fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '.03em', color: 'var(--text-muted)', background: 'var(--bg-page, #f3f4f6)',
              borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
              position: 'sticky', top: 0, zIndex: 5
            }}>
              {society} <span style={{ fontWeight: 500, textTransform: 'none' }}>({list.length})</span>
            </div>
            {list.map((c) => {
              const isActive = c.status === 'active';
              const isUnpaid = (c.outstandingBalance || 0) > 0;
              // Non-active rows are tinted by status — same treatment for both: inactive
              // light grey (text + row bg), suspended light red (text + row bg).
              const greyText = c.status === 'suspended' ? '#f87171' : '#9ca3af';
              const rowBg = c.status === 'suspended' ? '#fef2f2' : (isActive ? 'var(--bg-card)' : '#f3f4f6');
              const fullAddress = [c.address?.line1, c.address?.landmark].filter(Boolean).join(', ');
              return (
                <div
                  key={c._id}
                  onClick={() => navigate(`/agent/customer/${c._id}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                    borderBottom: '1px solid var(--border)', background: rowBg, cursor: 'pointer'
                  }}
                >
                  <div style={{
                    width: 46, height: 46, borderRadius: '50%', background: isActive ? colorFor(c.name) : greyText,
                    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '1.05rem', flexShrink: 0
                  }}>
                    {c.name?.[0]?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '1.05rem', color: isActive ? 'var(--text)' : greyText }}>{c.name}</div>
                    <div style={{ fontSize: '.88rem', color: isActive ? 'var(--text-muted)' : greyText }}>
                      {isActive ? 'Active' : c.status}
                      {c.catv?.expiryDate ? ` · Expiry: ${new Date(c.catv.expiryDate).toLocaleDateString('en-GB')}` : ''}
                    </div>
                    {fullAddress && (
                      <div style={{
                        fontSize: '.85rem', color: isActive ? 'var(--text-muted)' : greyText, marginTop: 1,
                        display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                      }}>
                        <MapPin size={12} style={{ flexShrink: 0 }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{fullAddress}</span>
                      </div>
                    )}
                    {c.pendingReason && (
                      <div style={{
                        fontSize: '.82rem', color: '#dc2626', marginTop: 2, fontWeight: 600,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                      }}>
                        ⚠ Collection Pending: {c.pendingReason.reason}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                    <div style={{
                      background: isUnpaid ? '#16a34a' : 'var(--bg-sidebar)', color: '#fff', borderRadius: 16, padding: '5px 12px',
                      fontSize: '.9rem', fontWeight: 700
                    }}>
                      {(c.outstandingBalance || 0) < 0 ? `Adv ₹${Math.abs(c.outstandingBalance)}` : `₹${c.outstandingBalance || 0}`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))
      )}

      {canAdd && (
        <button
          onClick={() => setAddOpen(true)}
          style={{
            position: 'fixed', bottom: 76, right: 'max(20px, calc(50% - 220px))',
            background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 24,
            padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 6,
            fontWeight: 700, fontSize: '.85rem', boxShadow: '0 4px 12px rgba(0,0,0,.2)', cursor: 'pointer'
          }}
        >
          <Plus size={18} /> Add Customer
        </button>
      )}

      {addOpen && (
        <AddCustomerModal
          defaultArea={user?.assignedAreas?.[0] || ''}
          onClose={() => setAddOpen(false)}
          onCreated={() => { setAddOpen(false); load(); }}
        />
      )}

      {filterOpen && (
        <div onClick={() => setFilterOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
          alignItems: 'flex-end', justifyContent: 'center', zIndex: 100
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: '16px 16px 0 0', padding: 18, width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 700 }}>Filters</div>
              <button onClick={() => setFilterOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Payment Type</label>
              <div style={{ display: 'flex', gap: 16 }}>
                {['paid', 'unpaid', 'advance'].map((v) => (
                  <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.85rem', textTransform: 'capitalize', cursor: 'pointer' }}>
                    <input
                      type="radio" name="paymentType" checked={draft.paymentType === v}
                      onChange={() => setDraft({ ...draft, paymentType: draft.paymentType === v ? '' : v })}
                    /> {v}{typeCounts ? ` (${typeCounts[v] ?? 0})` : ''}
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Customer Status</label>
              <select className="form-control" value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
                <option value="">Any</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                Bill Area {draft.area.length > 0 && `(${draft.area.length} selected)`}
              </label>
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, maxHeight: 170, overflowY: 'auto', padding: '2px 10px' }}>
                {areaOptions.length === 0 && (
                  <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', padding: '8px 0' }}>No areas found</div>
                )}
                {areaOptions.map((a) => (
                  <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', padding: '6px 0', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={draft.area.includes(a)}
                      onChange={() => setDraft((d) => ({
                        ...d,
                        area: d.area.includes(a) ? d.area.filter((x) => x !== a) : [...d.area, a],
                      }))}
                    />
                    {a}
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Due Date (on/before)</label>
              <input className="form-control" type="date" value={draft.dueDate} onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })} />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { setDraft(EMPTY_ADV); setAdv(EMPTY_ADV); setFilterOpen(false); }}
                style={{ flex: 1, background: 'var(--bg-sidebar)', color: '#fff', border: 'none', borderRadius: 22, padding: '11px 0', fontWeight: 700, cursor: 'pointer' }}
              >
                Reset
              </button>
              <button
                onClick={() => { setAdv(draft); setFilterOpen(false); }}
                style={{ flex: 1, background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 22, padding: '11px 0', fontWeight: 700, cursor: 'pointer' }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
